import React, { useState } from 'react';
import { Modal, Button, Switch, Radio, Divider, Space, Tooltip, Typography, message } from 'antd';
import { RocketOutlined, WarningOutlined } from '@ant-design/icons';
import { lanzarSync } from '../../api/brevoSyncApi';

const { Text, Title } = Typography;

interface SistemasModalProps {
  visible: boolean;
  hasRunning: boolean;
  onClose: () => void;
  onLaunched: () => void;
}

interface ConfirmAction {
  tipo: string;
  args: string;
  label: string;
  warning?: string;
}

const SistemasModal: React.FC<SistemasModalProps> = ({ visible, hasRunning, onClose, onLaunched }) => {
  // Sync diaria options
  const [resetDiario, setResetDiario] = useState(true);
  const [dryRunDiario, setDryRunDiario] = useState(false);
  const [faseDiario, setFaseDiario] = useState<'ambas' | 'solo-fase-a' | 'solo-fase-b'>('ambas');

  // Carga inicial options
  const [resetInicial, setResetInicial] = useState(true);
  const [dryRunInicial, setDryRunInicial] = useState(false);

  // Confirmation
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [launching, setLaunching] = useState(false);

  const buildArgs = (flags: string[]): string => flags.filter(Boolean).join(' ');

  const handleLaunchDiaria = () => {
    const flags: string[] = [];
    if (resetDiario) flags.push('--reset');
    if (dryRunDiario) flags.push('--dry-run');
    if (faseDiario === 'solo-fase-a') flags.push('--solo-fase-a');
    if (faseDiario === 'solo-fase-b') flags.push('--solo-fase-b');
    setConfirmAction({
      tipo: 'diario',
      args: buildArgs(flags),
      label: 'Sincronización diaria',
    });
  };

  const handleLaunchInicial = () => {
    const flags: string[] = [];
    if (resetInicial) flags.push('--reset');
    if (dryRunInicial) flags.push('--dry-run');
    setConfirmAction({
      tipo: 'inicial',
      args: buildArgs(flags),
      label: 'Carga inicial',
      warning: 'La carga inicial puede tardar varias horas.',
    });
  };

  const handleLaunchSetup = () => {
    setConfirmAction({
      tipo: 'setup_atributos',
      args: '',
      label: 'Setup de atributos',
    });
  };

  const handleLaunchSeed = () => {
    setConfirmAction({
      tipo: 'seed_control',
      args: '',
      label: 'Seed tabla de control',
    });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setLaunching(true);
    try {
      await lanzarSync({ tipo: confirmAction.tipo, args: confirmAction.args });
      message.success(`${confirmAction.label} lanzada correctamente`);
      setConfirmAction(null);
      onClose();
      onLaunched();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        message.warning('Ya hay una sincronización en curso');
      } else {
        message.error('Error al lanzar la sincronización');
      }
    } finally {
      setLaunching(false);
    }
  };

  const launchButton = (label: string, onClick: () => void) => (
    <Tooltip title={hasRunning ? 'Hay una ejecución en curso' : undefined}>
      <Button
        icon={<RocketOutlined />}
        onClick={onClick}
        disabled={hasRunning}
        size="small"
      >
        {label}
      </Button>
    </Tooltip>
  );

  return (
    <>
      <Modal
        open={visible}
        title="Panel de Sistemas"
        onCancel={onClose}
        footer={null}
        width={600}
      >
        {/* Sincronización diaria */}
        <Title level={5} style={{ marginTop: 0 }}>Sincronización diaria</Title>
        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 8 }}>
          <Space size="large">
            <span><Switch checked={resetDiario} onChange={setResetDiario} size="small" /> <Text>--reset</Text></span>
            <span><Switch checked={dryRunDiario} onChange={setDryRunDiario} size="small" /> <Text>--dry-run</Text></span>
          </Space>
          <div>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>Fase:</Text>
            <Radio.Group value={faseDiario} onChange={e => setFaseDiario(e.target.value)} size="small">
              <Radio.Button value="ambas">Ambas</Radio.Button>
              <Radio.Button value="solo-fase-a">Solo Fase A</Radio.Button>
              <Radio.Button value="solo-fase-b">Solo Fase B</Radio.Button>
            </Radio.Group>
          </div>
          {launchButton('Lanzar diaria', handleLaunchDiaria)}
        </Space>

        <Divider style={{ margin: '12px 0' }} />

        {/* Carga inicial */}
        <Title level={5} style={{ marginTop: 0 }}>Carga inicial</Title>
        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 8 }}>
          <Space size="large">
            <span><Switch checked={resetInicial} onChange={setResetInicial} size="small" /> <Text>--reset</Text></span>
            <span><Switch checked={dryRunInicial} onChange={setDryRunInicial} size="small" /> <Text>--dry-run</Text></span>
          </Space>
          {launchButton('Lanzar carga inicial', handleLaunchInicial)}
        </Space>

        <Divider style={{ margin: '12px 0' }} />

        {/* Setup de atributos */}
        <Title level={5} style={{ marginTop: 0 }}>Setup de atributos</Title>
        <div style={{ marginBottom: 8 }}>
          {launchButton('Lanzar setup atributos', handleLaunchSetup)}
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Seed tabla de control */}
        <Title level={5} style={{ marginTop: 0 }}>Seed tabla de control</Title>
        <div style={{ marginBottom: 8 }}>
          {launchButton('Lanzar seed', handleLaunchSeed)}
        </div>
      </Modal>

      {/* Modal de confirmación */}
      <Modal
        open={!!confirmAction}
        title="Confirmar lanzamiento"
        okText="Lanzar"
        cancelText="Cancelar"
        onOk={handleConfirm}
        onCancel={() => setConfirmAction(null)}
        confirmLoading={launching}
      >
        {confirmAction && (
          <Space direction="vertical" size="small">
            <Text>Vas a lanzar: <Text strong>{confirmAction.label}</Text></Text>
            <Text type="secondary">
              Parámetros: <Text code>{confirmAction.args || 'ninguno'}</Text>
            </Text>
            {confirmAction.warning && (
              <Text type="warning"><WarningOutlined /> {confirmAction.warning}</Text>
            )}
          </Space>
        )}
      </Modal>
    </>
  );
};

export default SistemasModal;
